<template>
    <form class="form-container" @submit.prevent="submitForm">
        <div class="form-row">
            <label class="form-label p-large-semibold black-default">Name:</label>
            <input v-model="name" type="text" class="msg-input p-large-reg" :class="{ 'disabled-input': !editInfo }" :disabled="!editInfo" @focus="scrollIntoView" @keydown.enter="handleEnterKey" />
        </div>
        <div v-if="nameError" class="error-message medium-reg">{{ nameError }}</div>
        
        <div v-if="showEmail" class="form-row">
            <label class="form-label p-large-semibold black-default">Email:</label>
            <input v-model="email" type="email" class="msg-input p-large-reg" :class="{ 'disabled-input': !editInfo }" placeholder="name@email.com" :disabled="!editInfo" @focus="scrollIntoView" @keydown.enter="handleEnterKey" />
        </div>
        <div v-if="showEmail && emailError" class="error-message medium-reg">{{ emailError }}</div>
        
        <div v-if="showPhone" class="form-row">
            <label class="form-label p-large-semibold black-default">Phone:</label>
            <input v-model="phone" type="tel" class="msg-input p-large-reg" :class="{ 'disabled-input': !editInfo }" :disabled="!editInfo" @focus="scrollIntoView" @keydown.enter="handleEnterKey" />
        </div>
        <div v-if="showPhone && phoneError" class="error-message medium-reg">{{ phoneError }}</div>
        
        <div v-if="editInfo" class="consent-checkboxes-wrapper">
          <div v-if="showPhone" class="consent-checkbox-container">
                <label class="consent-checkbox-label">
                    <input 
                        v-model="phoneConsent" 
                        type="checkbox" 
                        class="consent-checkbox"
                        :disabled="!editInfo"
                    />
                    <span class="consent-text">I consent to receiving text messages from {{ seller }}. Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP to opt out.</span>
                </label>
            </div>
            <div v-if="showPhone && phoneConsentError" class="error-message medium-reg">{{ phoneConsentError }}</div>
            <div class="consent-checkbox-container">
                <label class="consent-checkbox-label">
                    <input 
                        v-model="privacyConsent" 
                        type="checkbox" 
                        class="consent-checkbox"
                        :disabled="!editInfo"
                    />
                    <span class="consent-text">I agree with the <a href="https://www.iterating.tv/privacy.html" target="_blank" class="consent-link">privacy policy</a> and <a href="https://www.iterating.tv/terms.html" target="_blank" class="consent-link">terms of service</a>.</span>
                </label>
            </div>
            <div v-if="privacyConsentError" class="error-message medium-reg">{{ privacyConsentError }}</div>
        </div>
        
        <div v-if="editInfo && !showPhone" class="disclaimer-text">
            By clicking {{ showEditButtons ? 'save' : 'send' }}, you consent to receiving text messages. Message and data rates may apply.
        </div>
        
        <div v-if="editInfo && !showEditButtons" class="button-group">
            <button type="submit" class="solid-button p-med-semibold">Send</button>
        </div>
        <div v-else-if="editInfo && showEditButtons" class="button-group">
            <button type="button" @click="cancel" class="solid-button p-med-semibold grayed-btn">Cancel</button>
            <button type="button" @click="updateForm" class="solid-button p-med-semibold">Save</button>
        </div>
        <div v-else class="button-group" style="margin-left: 70px;">
            <button type="button" @click="edit()" class="pill-button p-med-semibold full-width">Edit</button>
        </div>
    </form>
</template>

<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { saveContactInfo, updateContactInfo } from '@/services/ContactInfoService';
import { useLeadStore } from '@/stores/useLeadStore';
import { useSellerStore } from '@/stores/useSellerStore';
import { storeToRefs } from 'pinia';
import { useWindowStateStore } from "@/stores/useWindowStateStore";
// Props
const props = defineProps({
  inputMode: {
    type: String,
    default: 'all',
    validator: (value) => ['email', 'phone', 'all'].includes(value)
  },
  edit: {
    type: Boolean,
    default: true
  },
  initialData: {
    type: Object,
    default: null
  }
});

// Computed properties to determine which inputs to show
const showEmail = computed(() => props.inputMode === 'email' || props.inputMode === 'all');
const showPhone = computed(() => props.inputMode === 'phone' || props.inputMode === 'all');

const leadStore = useLeadStore()
const { lead } = storeToRefs(leadStore)
const editInfo = ref(props.edit)
const showEditButtons = ref(false)

/** Prefer lead fields; fall back to initialData when absent or empty */
function resolvedField(field) {
    return lead.value?.[field] || props.initialData?.[field] || ''
}

/** Update name/email/phone from lead → initialData (no consent changes). */
function setMergedRefsOnly() {
    name.value = resolvedField('name')
    email.value = resolvedField('email')
    phone.value = resolvedField('phone')
}

/** Initial load / lead & initial updates: merged fields plus consent pre-fill when completing known contact. */
function hydrateFromLeadOrInitial() {
    setMergedRefsOnly()
    if (name.value && (email.value || phone.value)) {
        phoneConsent.value = true
        privacyConsent.value = true
    }
}

const name = ref('')
const email = ref('')
const phone = ref('')
const phoneConsent = ref(false)
const privacyConsent = ref(false)

hydrateFromLeadOrInitial()

watch([lead, () => props.initialData], hydrateFromLeadOrInitial, { deep: true, immediate: true })


const nameError = ref('')
const emailError = ref('')
const phoneError = ref('')
const phoneConsentError = ref('')
const privacyConsentError = ref('')

const sellerStore = useSellerStore()
const { seller } = storeToRefs(sellerStore)

const windowStateStore = useWindowStateStore();

onMounted(() => {
    sellerStore.initialize()
})

const submitForm = () => {
    if (validateForm()) {
        saveContactInfo({ name: name.value, email: email.value, phone: phone.value.replace(/\D/g, '') })
        editInfo.value = false
        windowStateStore.showContactForm = false;
    }
};

const cancel = () => {
    editInfo.value = false
    nameError.value = ''
    emailError.value = ''
    phoneError.value = ''
    phoneConsentError.value = ''
    privacyConsentError.value = ''
    phoneConsent.value = false
    privacyConsent.value = false
    setMergedRefsOnly()
}

const updateForm = () => {
    if (validateForm()) {
        // Check if values have actually changed
        const currentValues = {
            name: name.value,
            email: email.value,
            phone: phone.value.replace(/\D/g, '')
        };
        
        const savedValues = {
            name: lead.value.name || '',
            email: lead.value.email || '',
            phone: lead.value.phone || ''
        };
        
        // Compare current values with saved values
        const hasChanges = currentValues.name !== savedValues.name ||
                          currentValues.email !== savedValues.email ||
                          currentValues.phone !== savedValues.phone;
        
        if (hasChanges) {
            updateContactInfo(currentValues)
            editInfo.value = false
        } else {
            // No changes detected, perform same function as cancel
            console.log("No changes detected, canceling edit")
            cancel()
        }
        windowStateStore.showContactForm = false;
    }
}

const edit = () => {
    name.value = resolvedField('name')
    email.value = resolvedField('email')
    phone.value = resolvedField('phone')
    editInfo.value = true
    showEditButtons.value = true
    // Auto-check both consent checkboxes since user has already consented
    privacyConsent.value = true
    phoneConsent.value = true
}

const scrollIntoView = (event) => {
    setTimeout(() => {
    event.target.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest'
        });
    }, 250);
}

const handleEnterKey = (event) => {
    event.preventDefault();
    
    // Only process if the form is in edit mode
    if (!editInfo.value) {
        return;
    }
    
    // If we're in edit mode with edit buttons showing, update the form
    if (showEditButtons.value) {
        updateForm();
    } else {
        // Otherwise, submit the form
        submitForm();
    }
}

const validateForm = () => {
    let isValid = true;
    // Validate name
    if (name.value.length < 2) {
        nameError.value = 'Name must be at least 2 characters long'
        isValid = false
    } else {
        nameError.value = ''
    }
    
    // Validate email only if email input is shown
    if (showEmail.value) {
        if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/.test(email.value)) {
            emailError.value = 'Invalid email address';
            isValid = false;
        } else {
            emailError.value = '';
        }
    }
    
    // Validate phone only if phone input is shown
    if (showPhone.value) {
        // Remove all non-digit characters for validation
        const cleanPhone = phone.value.replace(/\D/g, '');
        
        // Check if it's a valid 10-digit phone number after cleaning
        if (cleanPhone.length !== 10) {
            phoneError.value = 'Phone number must be 10 digits';
            isValid = false;
        } else {
            phoneError.value = '';
        }
        
        // Validate consent checkbox if phone number is provided
        if (cleanPhone.length === 10 && !phoneConsent.value) {
            phoneConsentError.value = 'You must consent to receiving text messages to provide a phone number';
            isValid = false;
        } else {
            phoneConsentError.value = '';
        }
    }
    
    // Validate privacy consent checkbox
    if (!privacyConsent.value) {
        privacyConsentError.value = 'You must agree to the privacy policy and terms of service';
        isValid = false;
    } else {
        privacyConsentError.value = '';
    }
    
    return isValid;
}

</script>

<style>
.form-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.form-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.form-label {
    min-width: 60px;
    flex-shrink: 0;
}

.disabled-input {
    background-color: #f5f5f5;
    color: #666;
    cursor: not-allowed;
    opacity: 0.8;
}

.error-message {
    color: var(--negative-default);
    margin-top: -5px;
}

.button-group {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
}

.button-group > button {
    flex: 1
}

.button-group > button:not(:last-child) {
    margin-right: 10px;
}

.full-width {
    width: 100%;
}

.disclaimer-text {
    font-size: 12px;
    color: #666;
    text-align: center;
    margin: 0;
    line-height: 1.3;
}

.consent-checkboxes-wrapper {
    margin: 10px 0;
}

.consent-checkbox-container {
    margin: 5px 0;
    /*margin-left: 70px; /* Align with input fields (label width + gap) */
}

.consent-checkbox-label {
    display: flex;
    align-items: start;
    gap: 8px;
    cursor: pointer;
    font-size: 12px;
    color: #666;
    line-height: 1.3;
}

.consent-checkbox {
    margin: 0;
    flex-shrink: 0;
    margin-top: 2px;
}

.consent-text {
    flex: 1;
}

.consent-link {
    color: #666;
    text-decoration: underline;
    cursor: pointer;
}

.consent-link:hover {
    color: #333;
}
</style>
  
    